import { GoogleMap, Marker, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { Fragment, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { positionReports } from './monitoringData';

const defaultCenter = { lat: -2.5489, lng: 118.0149 };

const Maps = ({ dataSource }) => {

    const data = useMemo(() => positionReports(dataSource), [dataSource]);
    // console.log('data:', data);

    const mapRef = useRef(null);

    // Calculate center coordinates for display (average of all coordinates)
    const center = useMemo(() => {
        if (data.length === 0) return defaultCenter;

        const totalLat = data.reduce((sum, point) => sum + point.lat, 0);
        const totalLng = data.reduce((sum, point) => sum + point.lng, 0);

        return {
            lat: totalLat / data.length,
            lng: totalLng / data.length
        };
    }, [data]);
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyDgCJRdIE3hpeL8oR9IP5LQQrzn6Olb-Ow",
        preventGoogleFontsLoading: true
    });

    const handleMapLoad = (map) => {
        mapRef.current = map;

        if (data.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            data.forEach((point) => {
                bounds.extend({ lat: point.lat, lng: point.lng });
            });

            // Add padding to bounds for better visualization
            map.fitBounds(bounds, {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50
            });
        } else {
            // Jika tidak ada data, center ke default location dengan zoom untuk seluruh Indonesia
            map.setCenter(defaultCenter);
            map.setZoom(5); // Zoom level untuk menampilkan seluruh Indonesia
        }
    };

    if (loadError) {
        return (
            <div className="w-full h-full flex flex-col gap-6">
                <div className="relative flex-1 rounded-2xl overflow-hidden bg-gray-100">
                    <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <p className="text-red-600 font-semibold mb-2">Failed to load Google Maps</p>
                            <p className="text-sm text-gray-600">Please check your internet connection or disable ad blockers</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="w-full h-full flex flex-col gap-6">
                {/* Loading Map */}
                <div className="relative flex-1 rounded-2xl overflow-hidden bg-gray-100 animate-pulse">
                    <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gray-300 rounded-full mx-auto mb-4 animate-spin"></div>
                            <p className="text-gray-500 font-semibold">Loading Maps...</p>
                            <p className="text-sm text-gray-400 mt-1">Initializing Google Maps</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col gap-6">
            {/* Map Container */}
            <div className="relative flex-1 rounded-b-xl overflow-hidden border-2 border-white/20">
                <GoogleMap
                    options={{
                        mapTypeId: "hybrid"
                    }}
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={center}
                    zoom={data.length > 0 ? 5 : 4}
                    onLoad={handleMapLoad}
                >
                    {data.length > 0 ? (
                        data.map((point, index) => (
                            <Fragment key={point.key}>
                                <Marker
                                    key={`marker-${index}`}
                                    position={{ lat: point.lat, lng: point.lng }}
                                    icon={{
                                        url: "/assets/ship-z.png",
                                        scaledSize: new window.google.maps.Size(80, 80),
                                        anchor: new window.google.maps.Point(40, 40)
                                    }}
                                    title={point.ShipName || "Vessel Position"}
                                />
                                <OverlayView
                                    key={`label-${index}`}
                                    position={{ lat: point.lat, lng: point.lng }}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            transform: 'translate(-50%, 35px)',
                                            backgroundColor: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: 'black',
                                            whiteSpace: 'nowrap',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {point.ShipName || point.ShipCode || "Vessel Position"}
                                    </div>
                                </OverlayView>
                            </Fragment>
                        ))
                    ) : null}
                </GoogleMap>
            </div>
        </div>
    );
};

Maps.propTypes = { dataSource: PropTypes.array };
export default Maps;
